import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SearchField from '../../components/SearchField';
import api from '../../services/api';
import './AuthorizationsTable.css';

/**
 * Who has authorized this app, and who used to.
 *
 * This was a collapsed list inside the metrics scroll, sorted only by whenever
 * the rows happened to come back. The questions people actually bring to it —
 * who is on it now, who left, who is this person, when did they join — all need
 * sorting, filtering and search, so it is a table.
 *
 * Filtering and sorting happen here rather than on the server: the endpoint
 * returns every grant in one call, and a round-trip per keystroke would be
 * slower and no more correct.
 */
const STATES = [
  { key: 'all', label: 'Everyone' },
  { key: 'active', label: 'Active' },
  { key: 'paused', label: 'Paused' },
  { key: 'revoked', label: 'Revoked' },
];

const STATE_LABEL = { active: 'Active', paused: 'Paused', revoked: 'Revoked' };

/* Who the grant is for. A grant carries no group itself — the privacy realm it
 * points at does — so an authorization made against a group realm is acting for
 * that group, not for the person who happened to press approve. Worth telling
 * apart: one person leaving a group is not the same as one person revoking. */
const SCOPES = [
  { key: 'all', label: 'Everyone' },
  { key: 'personal', label: 'People' },
  { key: 'group', label: 'Groups' },
];

function AuthorizationsTable({ appId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [state, setState] = useState('all');
  const [scope, setScope] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ by: 'authorized_at', dir: 'desc' });

  const load = useCallback(async () => {
    if (!appId) return;
    setLoading(true); setError(null);
    try {
      // limit is generous because filtering is local; the endpoint pages at 50
      // by default, which would hide people behind a control nobody expects.
      setData(await api.request(`/third-party/${appId}/authorized-users?limit=1000`));
    } catch (err) {
      setError(err.message || 'Could not load authorizations.');
    }
    setLoading(false);
  }, [appId]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    let out = (data?.users || []);
    if (state !== 'all') out = out.filter(u => u.state === state);
    if (scope !== 'all') out = out.filter(u => (u.scope || 'personal') === scope);
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter(u =>
        (u.username || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.group_name || '').toLowerCase().includes(q)
        || (u.user_id || '').toLowerCase().includes(q));
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => {
      if (sort.by === 'person') {
        return dir * String(a.username || a.email || a.user_id)
          .localeCompare(String(b.username || b.email || b.user_id));
      }
      if (sort.by === 'state') return dir * String(a.state).localeCompare(String(b.state));
      if (sort.by === 'scope') {
        return dir * String(a.scope || 'personal').localeCompare(String(b.scope || 'personal'));
      }
      // Missing dates sort last whichever way the column is pointing: a row with
      // no timestamp is not "oldest", it is unknown.
      const av = a[sort.by] ? Date.parse(a[sort.by]) : null;
      const bv = b[sort.by] ? Date.parse(b[sort.by]) : null;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return dir * (av - bv);
    });
  }, [data, state, scope, query, sort]);

  const clickSort = (by) => setSort(s => ({
    by,
    // A new column starts newest-first for dates and A–Z for names, which is
    // what people expect from each without having to click twice.
    dir: s.by === by ? (s.dir === 'asc' ? 'desc' : 'asc')
      : (by === 'person' || by === 'state' || by === 'scope' ? 'asc' : 'desc'),
  }));

  const arrow = (by) => sort.by !== by ? '' : sort.dir === 'asc' ? ' ↑' : ' ↓';
  const counts = data?.counts || {};

  if (loading) return <p className="auth__msg">Loading…</p>;
  if (error) return <p className="auth__msg auth__msg--bad">{error}</p>;

  return (
    <div className="auth">
      <div className="auth__controls">
        <div className="auth__states" role="group" aria-label="Filter by state">
          {STATES.map(s => {
            const n = s.key === 'all'
              ? (data?.total ?? 0)
              : (counts[s.key] || 0);
            return (
              <button
                key={s.key}
                className={state === s.key ? 'is-active' : ''}
                onClick={() => setState(s.key)}
              >
                {s.label} <span>{n}</span>
              </button>
            );
          })}
        </div>
        <div className="auth__states auth__states--scope" role="group" aria-label="Filter by who it is for">
          {SCOPES.map(sc => (
            <button
              key={sc.key}
              className={scope === sc.key ? 'is-active' : ''}
              onClick={() => setScope(sc.key)}
            >
              {sc.label} <span>{sc.key === 'all' ? (data?.total ?? 0) : (data?.scopes?.[sc.key] || 0)}</span>
            </button>
          ))}
        </div>
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder="Search name, email, group or id…"
          label="Search authorized users"
        />
      </div>

      {rows.length === 0 ? (
        <p className="auth__msg">
          {(data?.total ?? 0) === 0
            ? 'Nobody has authorized this app yet.'
            : 'Nobody matches that filter.'}
        </p>
      ) : (
        <table className="auth__table">
          <thead>
            <tr>
              <th><button onClick={() => clickSort('person')}>Person{arrow('person')}</button></th>
              <th><button onClick={() => clickSort('scope')}>For{arrow('scope')}</button></th>
              <th><button onClick={() => clickSort('state')}>State{arrow('state')}</button></th>
              <th><button onClick={() => clickSort('authorized_at')}>Authorized{arrow('authorized_at')}</button></th>
              <th><button onClick={() => clickSort('revoked_at')}>Revoked{arrow('revoked_at')}</button></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(u => (
              <tr key={u.ep_id} className={`is-${u.state}`}>
                <td>
                  <span className="auth__name">{u.username || u.email || u.user_id}</span>
                  {u.email && u.username && <span className="auth__email">{u.email}</span>}
                  {u.scope === 'group' && (
                    <span className="auth__scope" title="Authorized for a group, not just this person">
                      {u.group_name || 'group'}
                    </span>
                  )}
                </td>
                <td className="auth__for">{u.scope === 'group' ? 'Group' : 'Person'}</td>
                <td><span className={`auth__state is-${u.state}`}>{STATE_LABEL[u.state] || u.state}</span></td>
                <td className="auth__when">{u.authorized_at ? new Date(u.authorized_at).toLocaleDateString() : '—'}</td>
                <td className="auth__when">{u.revoked_at ? new Date(u.revoked_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rows.length > 0 && (
        <p className="auth__count">
          {rows.length} of {data?.total ?? rows.length} shown
        </p>
      )}
    </div>
  );
}

export default AuthorizationsTable;
